<template>
  <transition name="modal-fade">
    <div
      v-if="isVisible && visibleAnnouncements.length > 0"
      class="announcement-overlay"
    >
      <div class="announcement-modal">
        <!-- Image Container with Swipe Support -->
        <div
          class="image-container"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        >
          <transition :name="slideDirection" mode="out-in">
            <div
              :key="currentAnnouncement.id"
              class="image-wrapper"
              :class="{ clickable: !!currentAnnouncement.link_url }"
              @click="handleImageClick"
            >
              <img
                :src="currentAnnouncement.image_url"
                :alt="currentAnnouncement.name || 'Announcement'"
                class="announcement-image"
                loading="lazy"
                @error="handleImageError(currentAnnouncement.id)"
              />
            </div>
          </transition>
        </div>

        <!-- Dot Pagination (only if multiple announcements) -->
        <div v-if="visibleAnnouncements.length > 1" class="dot-pagination">
          <button
            v-for="(ann, index) in visibleAnnouncements"
            :key="ann.id"
            class="dot"
            :class="{ active: index === currentIndex }"
            @click="goToAnnouncement(index)"
          />
        </div>

        <!-- Footer: Dismiss Checkbox + Close Button -->
        <div class="modal-footer">
          <label
            :key="'checkbox-' + currentAnnouncement.id"
            class="dismiss-checkbox"
          >
            <input
              type="checkbox"
              :checked="isDismissed(currentAnnouncement.id)"
              @change="toggleDismiss(currentAnnouncement.id)"
            />
            <span class="checkmark"></span>
            <span class="dismiss-text">ไม่แสดงอีก</span>
          </label>

          <button class="close-btn" @click="handleClose">
            <span>ปิดหน้าต่าง</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AnnouncementPopup',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    announcements: {
      type: Array,
      default: () => []
    },
    auto: {
      type: Boolean,
      default: true
    },
    interval: {
      type: Number,
      default: 3000
    }
  },
  data() {
    return {
      isVisible: false,
      currentIndex: 0,
      localDismissedIds: [],
      failedImageIds: [],
      touchStartX: 0,
      touchEndX: 0,
      isAnimating: false,
      slideDirection: 'slide-left',
      autoScrollTimer: null,
      animationTimeout: null
    }
  },
  computed: {
    visibleAnnouncements() {
      // Filter out dismissed announcements and any whose image failed to load
      return this.announcements.filter(
        a =>
          !this.localDismissedIds.includes(a.id) &&
          !this.failedImageIds.includes(a.id)
      )
    },
    currentAnnouncement() {
      return this.visibleAnnouncements[this.currentIndex] || {}
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.isVisible = newVal
        if (newVal) {
          this.initializeState()
        } else {
          this.resetState()
        }
      },
      immediate: true
    },
    visibleAnnouncements(newVal) {
      if (newVal.length === 0) {
        this.$emit('input', false)
      } else if (this.currentIndex >= newVal.length) {
        this.currentIndex = Math.max(0, newVal.length - 1)
      }
      // Restart auto-scroll when visible announcements change
      this.resetAutoScroll()
    }
  },
  beforeDestroy() {
    this.stopAutoScroll()
    this.clearAnimationTimeout()
  },
  methods: {
    initializeState() {
      this.currentIndex = 0
      this.localDismissedIds = []
      this.failedImageIds = []
      this.startAutoScroll()
    },

    resetState() {
      this.stopAutoScroll()
      this.clearAnimationTimeout()
      this.animationTimeout = setTimeout(() => {
        this.currentIndex = 0
        this.isAnimating = false
      }, 300)
    },

    clearAnimationTimeout() {
      if (this.animationTimeout) {
        clearTimeout(this.animationTimeout)
        this.animationTimeout = null
      }
    },

    setAnimationComplete() {
      this.clearAnimationTimeout()
      this.animationTimeout = setTimeout(() => {
        this.isAnimating = false
      }, 300)
    },

    // Auto-scroll methods
    startAutoScroll() {
      if (!this.auto || this.visibleAnnouncements.length <= 1) return
      this.stopAutoScroll()
      this.autoScrollTimer = setInterval(() => {
        this.nextAnnouncement()
      }, this.interval)
    },

    stopAutoScroll() {
      if (this.autoScrollTimer) {
        clearInterval(this.autoScrollTimer)
        this.autoScrollTimer = null
      }
    },

    resetAutoScroll() {
      // Reset timer after user interaction
      if (this.auto && this.visibleAnnouncements.length > 1) {
        this.stopAutoScroll()
        this.startAutoScroll()
      }
    },

    handleClose() {
      this.$emit('input', false)
      this.$emit('close')
    },

    handleImageClick() {
      if (this.currentAnnouncement.link_url) {
        window.open(this.currentAnnouncement.link_url, '_blank')
        this.$emit('image-click', this.currentAnnouncement)
        this.handleClose()
      }
    },

    // A dead image_url has nothing useful to show — drop that announcement
    // instead of swapping in a fallback asset. When it was the only one the
    // visibleAnnouncements watcher closes the popup.
    handleImageError(announcementId) {
      if (announcementId == null) return
      if (!this.failedImageIds.includes(announcementId)) {
        this.failedImageIds = [...this.failedImageIds, announcementId]
      }
    },

    // Navigation methods
    goToAnnouncement(index) {
      if (this.isAnimating || index === this.currentIndex) return
      this.slideDirection =
        index > this.currentIndex ? 'slide-left' : 'slide-right'
      this.isAnimating = true
      this.currentIndex = index
      this.setAnimationComplete()
      // Reset auto-scroll after user clicks dot
      this.resetAutoScroll()
    },

    // Touch/Swipe support
    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX
      // Pause auto-scroll during touch
      this.stopAutoScroll()
    },

    handleTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].clientX
      const swipeDistance = this.touchStartX - this.touchEndX
      const minSwipeDistance = 50

      if (Math.abs(swipeDistance) > minSwipeDistance) {
        if (swipeDistance > 0) {
          this.nextAnnouncement()
        } else {
          this.prevAnnouncement()
        }
      }
      // Resume auto-scroll after swipe
      this.resetAutoScroll()
    },

    prevAnnouncement() {
      if (this.isAnimating || this.visibleAnnouncements.length <= 1) return
      this.slideDirection = 'slide-right'
      this.isAnimating = true

      this.currentIndex =
        this.currentIndex === 0
          ? this.visibleAnnouncements.length - 1
          : this.currentIndex - 1

      this.setAnimationComplete()
    },

    nextAnnouncement() {
      if (this.isAnimating || this.visibleAnnouncements.length <= 1) return
      this.slideDirection = 'slide-left'
      this.isAnimating = true

      this.currentIndex =
        (this.currentIndex + 1) % this.visibleAnnouncements.length

      this.setAnimationComplete()
    },

    // Dismiss functionality
    isDismissed(announcementId) {
      return this.localDismissedIds.includes(announcementId)
    },

    toggleDismiss(announcementId) {
      if (this.isDismissed(announcementId)) {
        // Uncheck - remove from local list
        this.localDismissedIds = this.localDismissedIds.filter(
          id => id !== announcementId
        )
      } else {
        // Check - immediately mark as dismissed locally
        // Use spread to create new array for Vue reactivity
        this.localDismissedIds = [...this.localDismissedIds, announcementId]
        // Emit dismiss event for parent to call API
        this.$emit('dismiss', announcementId)
      }
    },

    // Called by parent if API fails - revert the dismiss
    revertDismiss(announcementId) {
      this.localDismissedIds = this.localDismissedIds.filter(
        id => id !== announcementId
      )
    }
  }
}
</script>

<style lang="scss" scoped>
// Overlay
.announcement-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

// Modal Container
.announcement-modal {
  position: relative;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 20px;
  border: 2px solid rgba(212, 175, 55, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  padding: 5px;
  width: 100%;
  max-width: 340px;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 20px;
    max-width: 380px;
  }
}

// Modal Footer
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

// Close Button
.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.2);
  color: #ff6b6b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.6);
  }

  &:active {
    background: rgba(239, 68, 68, 0.4);
  }
}

// Image Container
.image-container {
  width: 100%;
  padding-top: 100%; // 1:1 aspect ratio
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}

.image-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  will-change: transform, opacity;

  &.clickable {
    cursor: pointer;

    .announcement-image:hover {
      transform: scale(1.02);
    }
  }
}

.announcement-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  transition: transform 0.3s ease;
  // GPU acceleration
  transform: translateZ(0);
  backface-visibility: hidden;
}

// Dot Pagination
.dot-pagination {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}

.dot {
  width: 8px;
  height: 8px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  padding: 0;

  &.active {
    background: #d4af37;
    transform: scale(1.5);
  }

  &:hover:not(.active) {
    background: rgba(255, 255, 255, 0.5);
  }
}

// Dismiss Checkbox
.dismiss-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;

  input[type='checkbox'] {
    display: none;
  }

  .checkmark {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(212, 175, 55, 0.5);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    flex-shrink: 0;

    &::after {
      content: '✓';
      color: #d4af37;
      font-size: 14px;
      font-weight: bold;
      opacity: 0;
      transition: opacity 0.15s ease;
    }
  }

  input:checked + .checkmark {
    background: rgba(212, 175, 55, 0.2);
    border-color: #d4af37;

    &::after {
      opacity: 1;
    }
  }

  .dismiss-text {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);

    @media (min-width: 768px) {
      font-size: 14px;
    }
  }

  &:hover .checkmark {
    border-color: #d4af37;
  }
}

// Slide Transitions - GPU accelerated with translate3d
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-left-enter {
  transform: translate3d(100%, 0, 0);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translate3d(-100%, 0, 0);
  opacity: 0;
}

.slide-right-enter {
  transform: translate3d(-100%, 0, 0);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translate3d(100%, 0, 0);
  opacity: 0;
}

// Modal Fade Transition - GPU accelerated
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .announcement-modal,
.modal-fade-leave-active .announcement-modal {
  transition: transform 0.25s ease;
}

.modal-fade-enter .announcement-modal,
.modal-fade-leave-to .announcement-modal {
  transform: scale3d(0.9, 0.9, 1);
}

// Responsive adjustments
@media (max-width: 480px) {
  .announcement-modal {
    max-width: calc(100vw - 32px);
  }
}
</style>
